import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Building, Users, UserPlus, Upload, Download,
  Settings, BookOpen, Heart, Loader2, Check, Crown, DollarSign,
  School, Trash2, Eye
} from 'lucide-react';

const OrganizationDashboard = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [organization, setOrganization] = useState(null);
  const [children, setChildren] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [teacherForm, setTeacherForm] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [addingTeacher, setAddingTeacher] = useState(false);

  const [curriculum, setCurriculum] = useState([]);
  const availableSubjects = [
    { id: 'alphabets', name: 'Alphabets', icon: BookOpen },
    { id: 'counting', name: 'Counting', icon: BookOpen },
    { id: 'reading', name: 'Reading (EL Education)', icon: BookOpen },
    { id: 'writing', name: 'Writing', icon: BookOpen },
    { id: 'math', name: 'Math (Envision)', icon: BookOpen },
    { id: 'science', name: 'Science', icon: BookOpen },
    { id: 'christian', name: 'Christian / Bible', icon: Heart },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [orgRes, childrenRes, classesRes, teachersRes] = await Promise.all([
        api.get('/organization/me'),
        api.get('/children'),
        api.get('/classes'),
        api.get('/organization/teachers')
      ]);
      setOrganization(orgRes.data);
      setChildren(childrenRes.data);
      setClasses(classesRes.data || []);
      setTeachers(teachersRes.data || []);
      setCurriculum(orgRes.data.custom_curriculum || []);
      
      // Redirect to onboarding if not completed
      if (!orgRes.data.onboarding_completed) {
        navigate('/organization/onboarding');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    setAddingTeacher(true);
    
    try {
      await api.post('/organization/add-teacher', teacherForm);
      toast.success('Teacher added successfully!');
      setTeacherForm({ name: '', email: '', password: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add teacher';
      toast.error(message);
    } finally {
      setAddingTeacher(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post(
        `/admin/bulk-upload-users?organization_id=${organization.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadResults(response.data);
      toast.success(`Successfully added ${response.data.created_count} users!`);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Bulk upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCurriculumChange = async (subjectId, checked) => {
    const newCurriculum = checked
      ? [...curriculum, subjectId]
      : curriculum.filter(s => s !== subjectId);
    
    setCurriculum(newCurriculum);
    
    try {
      await api.put('/organization/curriculum', newCurriculum);
      toast.success('Curriculum updated!');
    } catch (error) {
      toast.error('Failed to update curriculum');
      setCurriculum(curriculum); // Revert
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Building className="w-6 h-6 text-purple-500" />
                <div>
                  <h1 className="font-bold text-xl text-slate-800">{organization?.name}</h1>
                  <p className="text-sm text-slate-500 capitalize">{organization?.organization_type?.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-slate-500">Licenses</p>
                <p className="font-bold text-slate-800">
                  {organization?.used_licenses || 0} / {organization?.student_licenses || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Students</p>
                  <p className="text-2xl font-bold text-slate-900">{children.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Crown className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Available Licenses</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {(organization?.student_licenses || 0) - (organization?.used_licenses || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active Subjects</p>
                  <p className="text-2xl font-bold text-slate-900">{curriculum.length || 7}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Custom Curriculum */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-500" />
                Custom Curriculum
              </CardTitle>
              <CardDescription>Select subjects for your organization</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {availableSubjects.map(subject => (
                  <div key={subject.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <subject.icon className="w-5 h-5 text-slate-600" />
                      <span className="font-medium">{subject.name}</span>
                    </div>
                    <Checkbox
                      checked={curriculum.length === 0 || curriculum.includes(subject.id)}
                      onCheckedChange={(checked) => handleCurriculumChange(subject.id, checked)}
                      data-testid={`curriculum-${subject.id}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Add Teacher */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-green-500" />
                Add Teacher
              </CardTitle>
              <CardDescription>Create accounts for your teachers</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddTeacher} className="space-y-4">
                <div className="space-y-2">
                  <Label>Teacher Name</Label>
                  <Input
                    placeholder="Teacher Name"
                    value={teacherForm.name}
                    onChange={(e) => setTeacherForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    data-testid="teacher-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="teacher@organization.com"
                    value={teacherForm.email}
                    onChange={(e) => setTeacherForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                    data-testid="teacher-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    placeholder="Temporary password"
                    value={teacherForm.password}
                    onChange={(e) => setTeacherForm(prev => ({ ...prev, password: e.target.value }))}
                    required
                    data-testid="teacher-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={addingTeacher}
                  data-testid="add-teacher-btn"
                >
                  {addingTeacher ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Teacher
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Bulk Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-500" />
                Bulk Upload Students
              </CardTitle>
              <CardDescription>Upload a CSV file to add multiple students at once</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                <Upload className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 mb-2">Upload CSV file with columns:</p>
                <p className="text-sm text-slate-500 mb-4">name, email, password (optional)</p>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  onChange={handleBulkUpload}
                  className="hidden"
                  id="bulk-upload"
                />
                <label htmlFor="bulk-upload">
                  <Button
                    asChild
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={uploading}
                  >
                    <span>
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Choose File
                        </>
                      )}
                    </span>
                  </Button>
                </label>
              </div>

              {uploadResults && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg">
                  <p className="text-green-800 font-medium flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    {uploadResults.created_count} users created successfully
                  </p>
                  {uploadResults.errors?.length > 0 && (
                    <div className="mt-2 text-sm text-red-600">
                      <p className="font-medium">Errors:</p>
                      {uploadResults.errors.slice(0, 3).map((err, i) => (
                        <p key={i}>{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pricing Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-amber-500" />
                Enterprise Plan
              </CardTitle>
              <CardDescription>Your current subscription details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Student Licenses</span>
                  <span className="font-bold">{organization?.student_licenses}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Used Licenses</span>
                  <span className="font-bold">{organization?.used_licenses || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                  <span className="text-amber-700">Custom Pricing</span>
                  <span className="font-bold text-amber-700">
                    ${organization?.custom_pricing || 'Contact for quote'}
                  </span>
                </div>
                <p className="text-sm text-slate-500">
                  Need more licenses? Contact us for volume discounts.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Students Table */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Students</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Age Group</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>XP</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {children.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      No students added yet
                    </TableCell>
                  </TableRow>
                ) : (
                  children.map(child => (
                    <TableRow key={child.id}>
                      <TableCell className="font-medium">{child.name}</TableCell>
                      <TableCell className="capitalize">{child.age_group?.replace('_', ' ')}</TableCell>
                      <TableCell>{child.current_level}</TableCell>
                      <TableCell>{child.total_xp}</TableCell>
                      <TableCell>
                        <Link to={`/learn/${child.id}`}>
                          <Button size="sm" variant="outline">View</Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default OrganizationDashboard;
